// Ingresos calificables a partir de los movimientos de un extracto bancario (30-9-2026).
//
// Es el cálculo de los programas «bank statement» / non-QM de EE. UU. para autónomos: ingreso
// mensual medio = depósitos elegibles ÷ meses del extracto, menos un factor de gastos. Cada
// prestamista fija sus reglas (factor de gastos, qué depósitos excluye, umbral de depósito
// grande): aquí son ajustables y el resultado es una ESTIMACIÓN, no una decisión de crédito.
//
// Se ejecuta en el navegador sobre los movimientos que ya extrajo la herramienta.

const EXCLUDE = /(transfer|xfer|internal|from (savings|checking|account)|loan|advance|refund|reversal|reversed|return(ed)?\b|chargeback|credit adj|correction)/i;
const NSF = /(nsf|insufficient funds|overdraft|returned item|od fee|non-?sufficient)/i;

export function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined || value === "") return null;
  let s = String(value).trim();
  const negative = /^\(.*\)$/.test(s) || /^-/.test(s) || /-$/.test(s);
  s = s.replace(/[^0-9.,]/g, "");
  if (!s) return null;
  // 1.234,56 (europeo) vs 1,234.56 (anglosajón): manda el último separador.
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", ".");
  else s = s.replace(/,/g, "");
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -Math.abs(n) : n;
}

export function monthOf(dateValue) {
  const m = String(dateValue || "").match(/^(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : null;
}

export function computeIncome(transactions, opts = {}) {
  const expenseFactor = Math.min(Math.max(opts.expenseFactor ?? 0.5, 0), 0.95);
  const excludeTransfers = opts.excludeTransfers !== false;
  const largePct = opts.largeDepositPct ?? 0.5;

  const rows = [];
  for (const t of transactions || []) {
    const amount = toNumber(t?.amount);
    const month = monthOf(t?.date);
    if (amount === null || !month) continue;
    rows.push({ month, date: String(t.date).slice(0, 10), description: String(t.description || ""), amount });
  }

  const months = new Map();
  for (const r of rows) {
    if (!months.has(r.month)) months.set(r.month, { month: r.month, deposits: 0, eligible: 0, withdrawals: 0, count: 0 });
    const m = months.get(r.month);
    m.count += 1;
    if (r.amount > 0) {
      m.deposits += r.amount;
      if (!(excludeTransfers && EXCLUDE.test(r.description))) m.eligible += r.amount;
    } else {
      m.withdrawals += -r.amount;
    }
  }
  const list = [...months.values()].sort((a, b) => a.month.localeCompare(b.month));
  const round = (x) => Math.round(x * 100) / 100;
  const totalEligible = list.reduce((s, m) => s + m.eligible, 0);
  const monthCount = list.length;
  const avgMonthlyEligible = monthCount ? totalEligible / monthCount : 0;
  const qualifying = avgMonthlyEligible * (1 - expenseFactor);

  const excluded = rows.filter((r) => r.amount > 0 && excludeTransfers && EXCLUDE.test(r.description));
  const threshold = avgMonthlyEligible * largePct;
  const large = rows
    .filter((r) => r.amount > 0 && !(excludeTransfers && EXCLUDE.test(r.description)) && threshold > 0 && r.amount > threshold)
    .sort((a, b) => b.amount - a.amount);
  const nsf = rows.filter((r) => NSF.test(r.description));

  return {
    monthCount,
    months: list.map((m) => ({ ...m, deposits: round(m.deposits), eligible: round(m.eligible), withdrawals: round(m.withdrawals) })),
    from: rows.length ? rows.map((r) => r.date).sort()[0] : null,
    to: rows.length ? rows.map((r) => r.date).sort().slice(-1)[0] : null,
    totalEligible: round(totalEligible),
    avgMonthlyEligible: round(avgMonthlyEligible),
    qualifyingMonthly: round(qualifying),
    expenseFactor,
    excluded: excluded.map((r) => ({ ...r })),
    excludedTotal: round(excluded.reduce((s, r) => s + r.amount, 0)),
    largeThreshold: round(threshold),
    largeDeposits: large.map((r) => ({ ...r })),
    nsfCount: nsf.length,
    nsf: nsf.map((r) => ({ ...r })),
    // Un extracto que no llega a 12 meses no vale para el programa de 12: se avisa.
    partialEdges: list.length >= 2 && (list[0].count < 5 || list[list.length - 1].count < 5),
  };
}

export function incomeToCsv(r) {
  const q = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [["Month", "Deposits", "Eligible deposits", "Withdrawals", "Transactions"].map(q).join(",")];
  for (const m of r.months) lines.push([m.month, m.deposits, m.eligible, m.withdrawals, m.count].map(q).join(","));
  lines.push("");
  lines.push([q("Average monthly eligible deposits"), q(r.avgMonthlyEligible)].join(","));
  lines.push([q(`Expense factor`), q(`${Math.round(r.expenseFactor * 100)}%`)].join(","));
  lines.push([q("Estimated qualifying monthly income"), q(r.qualifyingMonthly)].join(","));
  if (r.largeDeposits.length) {
    lines.push("");
    lines.push([q("Large deposits (date)"), q("Description"), q("Amount")].join(","));
    for (const d of r.largeDeposits) lines.push([d.date, d.description, d.amount].map(q).join(","));
  }
  return lines.join("\r\n");
}
